import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon, ChevronRightIcon } from '@/components/icons';

import type { FilterGroupProps, FilterConfig, AdvancedFilters } from '@/types/filters';
import { FilterControl } from './FilterControl';

/**
 * Filter Group Component
 * 
 * Renders a collapsible group of related filters with:
 * - Expandable/collapsible interface
 * - Group icons and titles
 * - Filter count indicators
 * - Conditional visibility based on user role
 */
export function FilterGroup({
  config,
  filters,
  onFiltersChange,
  expanded,
  onToggle,
  filterOptions,
  className,
  userRole,
  onSearchSuggestions
}: FilterGroupProps) {
  
  // Filter configurations visible to current user and search
  const visibleFilters = useMemo(() => {
    return config.filters
      .filter(filterConfig => {
        // Check visibility - only filter out if explicitly set to false
        if (filterConfig.visible === false) return false;

        // Check dependencies - skip if no dependency
        if (filterConfig.dependency) {
          const depValue = (filters as any)[filterConfig.dependency.filterId];
          if (!filterConfig.dependency.values.includes(depValue)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => a.order - b.order);
  }, [config.filters, filters]);

  // Count active filters in this group
  const activeFilterCount = useMemo(() => {
    let count = 0;
    
    visibleFilters.forEach(filterConfig => {
      const value = (filters as any)[filterConfig.id];
      
      // Check if filter has active value
      if (filterConfig.type === 'toggle' || filterConfig.type === 'checkbox') {
        if (value === true) count++;
      } else if (filterConfig.type === 'multiselect' || filterConfig.type === 'tagSelect') {
        if (Array.isArray(value) && value.length > 0) count++;
      } else if (filterConfig.type === 'dateRange') {
        if (value && (value.from || value.to || value.preset)) count++;
      } else if (filterConfig.type === 'slider') {
        if (value && Array.isArray(value) && (value[0] !== 0 || value[1] !== 100)) count++;
      } else if (value && value !== '' && value !== null && value !== undefined) {
        count++;
      }
    });
    
    return count;
  }, [visibleFilters, filters]);

  // Get filter options for specific filter
  const getFilterOptions = (filterId: string) => {
    switch (filterId) {
      case 'assignedAgents':
        return filterOptions.agents.map(agent => ({
          value: agent.userId,
          label: agent.userName,
          icon: agent.avatar ? (
            <img 
              src={agent.avatar} 
              alt={agent.userName}
              className="w-4 h-4 rounded-full"
            />
          ) : (
            <div className="w-4 h-4 rounded-full bg-gray-300 flex items-center justify-center text-xs text-gray-600">
              {agent.userName.charAt(0).toUpperCase()}
            </div>
          )
        }));
        
      case 'assignedGroups':
        return filterOptions.groups.map(group => ({
          value: group.id,
          label: group.name,
          count: group.agentCount
        }));
        
      case 'companies':
        return filterOptions.companies.map(company => ({
          value: company.companyId,
          label: company.companyName,
          icon: company.isVip ? (
            <span className="text-yellow-500">★</span>
          ) : undefined
        }));
        
      case 'customers':
        return filterOptions.customers.map(customer => ({
          value: customer.userId,
          label: customer.userName,
          description: customer.userEmail
        }));
        
      case 'tags':
        return filterOptions.tags.map(tag => ({
          value: tag.tag,
          label: tag.tag,
          count: tag.count
        }));
        
      case 'statuses':
        return [
          { value: 'New', label: 'New', color: '#6B7280' },
          { value: 'Open', label: 'Open', color: '#10B981' },
          { value: 'Pending', label: 'Pending', color: '#3B82F6' },
          { value: 'OnHold', label: 'On Hold', color: '#F59E0B' },
          { value: 'Resolved', label: 'Resolved', color: '#8B5CF6' },
          { value: 'Closed', label: 'Closed', color: '#6B7280' },
          { value: 'Cancelled', label: 'Cancelled', color: '#EF4444' }
        ];
        
      case 'priorities':
        return [
          { value: 'Low', label: 'Low', color: '#10B981' },
          { value: 'Medium', label: 'Medium', color: '#F59E0B' },
          { value: 'High', label: 'High', color: '#EF4444' },
          { value: 'Urgent', label: 'Urgent', color: '#DC2626' },
          { value: 'Critical', label: 'Critical', color: '#7C2D12' }
        ];
        
      case 'sources':
        return [
          { value: 'Email', label: 'Email', icon: '📧' },
          { value: 'Web', label: 'Web Form', icon: '🌐' },
          { value: 'Phone', label: 'Phone', icon: '📞' },
          { value: 'Chat', label: 'Chat', icon: '💬' },
          { value: 'API', label: 'API', icon: '🔌' },
          { value: 'Social', label: 'Social', icon: '💬' }
        ];
        
      case 'types':
        return [
          { value: 'Bug', label: 'Bug' },
          { value: 'Feature Request', label: 'Feature Request' },
          { value: 'Question', label: 'Question' },
          { value: 'Incident', label: 'Incident' },
          { value: 'Task', label: 'Task' }
        ];
        
      default:
        return [];
    }
  };

  // Handle filter value change
  const handleFilterChange = (filterId: string, value: any) => {
    const newFilters = { ...filters, [filterId]: value };
    onFiltersChange(newFilters);
  };

  // Don't render if no visible filters
  if (visibleFilters.length === 0) {
    return null;
  }

  return (
    <div className={clsx('border-b border-gray-200', className)}>
      {/* Group Header */}
      {config.collapsible ? (
        <button
          onClick={onToggle}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
          aria-expanded={expanded}
          aria-controls={`filter-group-${config.id}`}
        >
          <div className="flex items-center">
            {config.icon && (
              <div className="mr-3 text-gray-400">
                {config.icon}
              </div>
            )}
            <h3 className="text-sm font-medium text-gray-900">
              {config.title}
            </h3>
            {activeFilterCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {activeFilterCount}
              </span>
            )}
          </div>
          <div className="flex items-center">
            {expanded ? (
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>
      ) : (
        <div className="px-4 py-3 flex items-center">
          {config.icon && (
            <div className="mr-3 text-gray-400">
              {config.icon}
            </div>
          )}
          <h3 className="text-sm font-medium text-gray-900">
            {config.title}
          </h3>
          {activeFilterCount > 0 && (
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              {activeFilterCount}
            </span>
          )}
        </div>
      )}

      {/* Group Content */}
      {(expanded || !config.collapsible) && (
        <div
          id={`filter-group-${config.id}`}
          className="px-4 pb-4 space-y-4"
        >
          {visibleFilters.map(filterConfig => (
            <FilterControl
              key={filterConfig.id}
              config={filterConfig}
              value={(filters as any)[filterConfig.id]}
              onChange={(value) => handleFilterChange(filterConfig.id, value)}
              options={getFilterOptions(filterConfig.id)}
              loading={filterOptions.loading.agents || filterOptions.loading.companies || filterOptions.loading.tags}
              onSearch={onSearchSuggestions ? (query) => onSearchSuggestions(filterConfig.id, query) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
